import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LandingNavItemComponent } from '../landing-nav-item/landing-nav-item.component';
import { LandingNavItem } from '../landing-nav-item/landing-nav-item.model';
import { boostThemeEnum } from '@app/services/page.service';
import { LastScoreResponse, ScoreService } from 'src/swagger/api.client.generated';
import * as moment from 'moment';
import { MARGIN_OF_SAFETY_THRESHOLDS, MarginOfSafetyLevel } from '@app/pages/score/margin-of-safety/margin-of-safety.models';
import { UserService } from '@app/authentication/user.service';
import { ScoreLocalService } from '@app/pages/score/score.service';

@Component({
  selector: 'app-landing-score',
  standalone: true,
  imports: [CommonModule,LandingNavItemComponent],
  templateUrl: './landing-score.component.html',
  styleUrls: ['./landing-score.component.scss']
})
export class LandingScoreComponent implements OnInit, OnDestroy {
  timeToScoreNavItem: LandingNavItem = {
    theme: boostThemeEnum.beautyberry,
    contentTheme: "feelings",
    title: "It's time to Score",
    content:
      "Score your halos and zombies now so that you can assess today's Safety Buffer!",
    action: "SCORE NOW",
    routerLink: "/score",
  };

  scoreNavItem: LandingNavItem = {
    theme: boostThemeEnum.beautyberry,
    contentTheme: "feelings",
    title: "It's time to Score",
    content:
      "Score your halos and zombies now so that you can assess today's Safety Buffer!",
    action: "SCORE NOW",
    routerLink: "/score",
  };

  score: LastScoreResponse | null = null;
  showTimeToScore: boolean = false;

  theme: boostThemeEnum;
  imageSrc: string;
  message: string;

  private expirationCheckInterval?: any;

  constructor(private scoreService: ScoreService, private userService: UserService, private scoreLocalService: ScoreLocalService) {}

  ngOnInit(): void {
    // Initial load - fetches score and updates display
    this.getLastScore();

    // Set up interval to periodically check if score window has expired
    // This allows the display to update automatically when the 6-hour window expires
    // without requiring user interaction or page refresh
    this.expirationCheckInterval = setInterval(() => {
      this.checkAndUpdateIfExpired();
    }, 60000); // Check every 1 minute (60000 ms)
  }

  ngOnDestroy(): void {
    if (this.expirationCheckInterval) {
      clearInterval(this.expirationCheckInterval);
    }
  }

  /**
   * Checks if the score window has expired and updates the display if needed
   * This is called periodically by the interval timer to detect when the 6-hour scoring window expires.
   * Uses cached score data to avoid unnecessary API calls.
   */
  private checkAndUpdateIfExpired(): void {
    const currentShouldAskToScore = this.scoreLocalService.shouldAskToScore();
    
    // Only update if the state has changed (e.g., score window expired)
    if (currentShouldAskToScore !== this.showTimeToScore) {
      // State has changed - update display using cached score
      // We use cached score here to avoid triggering an API call on every check
      // The cache is updated when getLastScore() is called (on initial load or when needed)
      const cachedScore = this.scoreLocalService.getCachedScore();
      this.updateDisplay(cachedScore, currentShouldAskToScore);
    }
  }

  /**
   * Updates the display based on score and shouldAskToScore flag
   * Shows either the "time to score" prompt or the current safety buffer score
   */
  private updateDisplay(score: LastScoreResponse | null, shouldAskToScore: boolean): void {
    this.showTimeToScore = shouldAskToScore;

    if (this.showTimeToScore) {
      this.score = null;
      // Check if there are 1 or 2 scores for today and update the content if so
      const scoresCountToday = this.scoreLocalService.getCachedScoresCountToday();
      if (scoresCountToday !== null && scoresCountToday == 1) {
        this.timeToScoreNavItem.content = "Scoring at the beginning and the end of the day helps you track how effective your safety buffering has been for the day, and if you need to think about adding extra safety in the future.";
      } else {
        // Reset to default content
        this.timeToScoreNavItem.content = "Score your halos and zombies now so that you can assess today's Safety Buffer!";
      }
    } else {
      if (score) {
        this.score = score;
        const threshold = MARGIN_OF_SAFETY_THRESHOLDS.find(
          (t) => score.totalScore >= t.minScore && score.totalScore <= t.maxScore
        );

        if (threshold) {
          this.theme = threshold.theme as boostThemeEnum;
          this.imageSrc = threshold.imageSrc;
          this.message = this.buildMessage(score.totalScore);
        }

        this.setUpScoreNavItem(score.totalScore);
      }
    }
  }

  /**
   * Fetches the last score from the service and updates the display
   * This triggers an API call if the cache is stale, otherwise uses cached data
   */
  getLastScore() {
    this.scoreLocalService.getLastScore().subscribe((score) => {
      const shouldAskToScore = this.scoreLocalService.shouldAskToScore();
      this.updateDisplay(score, shouldAskToScore);
    });
  }  

  setUpScoreNavItem(score: number): void {
    this.scoreNavItem = {
      theme: this.theme,
      contentTheme: "none",
      title: "TODAY'S SAFETY BUFFER",
      content: this.buildMessage(score),
      action: "",
      routerLink: "",
    };   
  }

  buildMessage(score: number): string {
    const threshold = MARGIN_OF_SAFETY_THRESHOLDS.find(
      (t) => score >= t.minScore && score <= t.maxScore
    );

    const usersPreferredName = this.userService.getPreferredName();
    let message = "";
    switch (threshold.level) {
      case MarginOfSafetyLevel.Great:
        message = `Hooray! Your Safety Buffer is looking extra strong today, ${usersPreferredName}. That’s wonderful! Remember you can keep it strong and green by making sure you include lots of star cards in today’s plan.`;
        break;
      case MarginOfSafetyLevel.Caution:
        message = `Hey ${usersPreferredName}, your Safety Buffer isn’t quite as robust today. No need to stress though! This is just a sign that you might need to add some extra safety into your day and think about dropping some activities to ensure you reach the end of the day feeling safe.`;
        break;
      case MarginOfSafetyLevel.Low:
        message = `Hi ${usersPreferredName}. It looks like you don’t have a very big Safety Buffer today. You should think about only doing the things you absolutely need to today, and fill your day with tons of safety. <strong>Don’t forget you can access your Flare-Up plan at any time using the Sanctuary button.</strong>`;
        break;
      default:
        message = "";
        break;
    }
    return message;
  }


}
